import { createApiMutation } from '../../../core/hooks/useMutationFactory';
import { ErrorSource } from '../../../core/constant/model/api/api';
import { getDataFromResponse } from '../../../core/constant/utils/react-query-utils/getDataFromResponse';
import { createApiError } from '../../../core/constant/utils/error.helper';
import { CompleteEnrollmentRequest, CompleteEnrollmentResponse, EnrollmentFormData, EnrollmentResponse } from '../types/enrollment';
import { completeEnrollment,  updateEnrollment } from '../api/enrollment.api';

import {
    resetEnrollmentCompleteStatus,
  resetEnrollmentStatus,
  updateEnrollmentComplete,
  updateEnrollmentCompleteFailure,
  updateEnrollmentCompleteSuccess,
  updateEnrollmentFailure,
  updateEnrollmentStart,
  updateEnrollmentSuccess,
} from '../../../core/store/slices/enrollment.slice';
import { setCompleteEnrollment, updateOnboardingBasicInfo } from '../../../core/store/slices/basicInfoSlice';

export const useUpdateEnrollmentMutation = () => {
  return createApiMutation<EnrollmentResponse, Partial<EnrollmentFormData>>({
    mutationFn: async (request, signal) => {      
      const response = await updateEnrollment(request.memberId as string, request, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Update enrollment failed', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.UPDATE_ENROLLMENT,
    loadingActions: {
      start: (dispatch) => dispatch(updateEnrollmentStart()),
      end: (dispatch) => dispatch(updateEnrollmentSuccess()),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      dispatch(updateEnrollmentSuccess());
    },
    onErrorHandler: (error, variables, context, dispatch) => {
      dispatch(updateEnrollmentFailure());
      // Error is already handled globally, so no need to dispatch any global error action here
    },
    onSettledHandler: (responseData, error, variables, context, dispatch) => {
      setTimeout(() => {
        dispatch(resetEnrollmentStatus());
      }, 3000);
    },
    module: 'Enrollment',
  });
};

export const useCompleteEnrollmentMutation = () => {
    return createApiMutation<CompleteEnrollmentResponse, CompleteEnrollmentRequest>({
      mutationFn: async (request, signal) => {
        const response = await completeEnrollment(request.memberId, request.payload, signal);
        const responseData = getDataFromResponse(response);
  
        if (responseData.statusCode && responseData.statusCode !== 200) {
          throw createApiError(responseData.message || 'Complete enrollment failed', responseData);
        }
  
        return responseData;
      },
      errorSource: ErrorSource.COMPLETE_ENROLLMENT,
      loadingActions: {
        start: (dispatch) => dispatch(updateEnrollmentComplete()),
        end: (dispatch) => dispatch(updateEnrollmentCompleteSuccess()),
      },
      onSuccessHandler: (responseData, variables, context, dispatch) => {
        dispatch(updateEnrollmentCompleteSuccess());
        dispatch(updateOnboardingBasicInfo(responseData.member));
        dispatch(setCompleteEnrollment(true));
      },
      onErrorHandler: (error, variables, context, dispatch) => {
        dispatch(updateEnrollmentCompleteFailure());
        // Error is already handled globally, so no need to dispatch any global error action here
      },
      onSettledHandler: (responseData, error, variables, context, dispatch) => {
        setTimeout(() => {
          dispatch(resetEnrollmentCompleteStatus());
        }, 3000);
      },
      module: 'Enrollment',
    });
  };
